import React, { useMemo, useState } from 'react'
import { AutoComplete, List, Tooltip } from 'antd'
import styled from 'styled-components'
import _ from 'lodash'
import { colours } from '@qflow/theme'

import { ColourButton, icons } from 'portal/lib/primitives'

import { AlertRecipients } from './AlertRecipients'
import { MATERIAL_WATCHLIST_KEYWORDS } from './MaterialKeyWords'
import {
  AdditionalText,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { EnableAlertRuleSwitch } from './EnableSwitch'

export const MaterialWatchlist = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}) => {
  const [autoCompleteValue, setAutoCompleteValue] = useState('')
  const [options, setOptions] = useState<{ value: string; label: string }[]>([])

  const allMaterialKeyWords = MATERIAL_WATCHLIST_KEYWORDS

  const materialKeywordOptions: { value: string; label: string }[] =
    useMemo(() => {
      if (!allMaterialKeyWords || !Array.isArray(allMaterialKeyWords)) {
        return []
      }
      return allMaterialKeyWords.map(keyword => ({
        value: keyword,
        label: keyword
      }))
    }, [allMaterialKeyWords])

  const addValueToAlertRules = (value: string) => {
    if (!value) return

    const updatedAlertRules = _.cloneDeep(alertRules)

    const parts = value
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)

    // Add each part if it's not already in the list
    parts.forEach(part => {
      if (!updatedAlertRules[alertKey].watchedMaterials.includes(part)) {
        updatedAlertRules[alertKey].watchedMaterials.push(part)
      }
    })

    // Sort watchedMaterials alphabetically
    updatedAlertRules[alertKey].watchedMaterials.sort((a, b) =>
      a.localeCompare(b)
    )
    setAlertRules(updatedAlertRules)

    // Clear the input field after adding
    setAutoCompleteValue('')
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      const value = e.target.value.trim()
      addValueToAlertRules(value)
    }
  }

  const handleSearch = value => {
    if (value.length >= 2) {
      // Simulate fetching data based on the input
      const filteredOptions = materialKeywordOptions.filter(option =>
        option.value.toLowerCase().includes(value.toLowerCase())
      )
      setOptions(filteredOptions)
    } else {
      setOptions([]) // Clear options if input is less than 2 characters
    }
  }

  const handleSelectClick = value => {
    addValueToAlertRules(value)
  }

  if (!alertRules[alertKey]) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Material Watchlist</Description>
          <AdditionalText>
            Notifies you if a material is delivered to site with one of these
            keywords in the name.
          </AdditionalText>
        </TextArea>
        <SettingArea>
          <EnableAlertRuleSwitch
            alertKey={alertKey}
            alertRules={alertRules}
            setAlertRules={setAlertRules}
            cannotEnable={cannotEnable}
            ariaLabel="Enable Material Watchlist"
          />
          <MaterialWatchlistWrapper>
            <AutoComplete
              id="material-autocomplete"
              allowClear
              options={options}
              onSearch={handleSearch}
              style={{ width: '404px' }}
              onChange={value => setAutoCompleteValue(value)}
              onInputKeyDown={handleKeyDown}
              onSelect={handleSelectClick}
              value={autoCompleteValue}
              placeholder="Enter or search material keywords"
            />
            <AdditionalText style={{ paddingLeft: '0.125rem' }}>
              <div>
                Add keywords to watchlist by typing them out and pressing Enter.
              </div>
              <div>
                Add multiple key terms at once by separating with commas.
              </div>
            </AdditionalText>
            <List
              size="small"
              bordered
              dataSource={alertRules[alertKey].watchedMaterials}
              locale={{
                emptyText: (
                  <div>
                    {!alertRules[alertKey].isEnabled ? (
                      'No keywords in watchlist.'
                    ) : (
                      <span style={{ color: colours.RED_DARKMODE }}>
                        One or more keywords required
                      </span>
                    )}
                  </div>
                )
              }}
              style={{
                marginTop: '0.5rem',
                backgroundColor: colours.WHITE,
                maxHeight: '13rem',
                overflowY: 'auto'
              }}
              renderItem={(material: string) => (
                <List.Item
                  style={{ padding: '0.25rem 0.25rem 0.25rem 0.625rem' }}
                >
                  <MaterialWrapper>
                    {material}
                    <Tooltip title="Remove keyword">
                      <ColourButton.BasicBorderless
                        name={`remove-keyword-${material.replace(/\s+/g, '-').toLowerCase()}`}
                        aria-label={'Remove keyword'}
                        style={{
                          padding: '0.25rem 9px',
                          borderRadius: '50%',
                          color: colours.RED_DARKMODE
                        }}
                        onClick={() => {
                          const updatedAlertRules = _.cloneDeep(alertRules)
                          updatedAlertRules[alertKey].watchedMaterials =
                            updatedAlertRules[alertKey].watchedMaterials.filter(
                              s => s !== material
                            )
                          setAlertRules(updatedAlertRules)
                        }}
                      >
                        <icons.TrashCan colour="currentColor" />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  </MaterialWrapper>
                </List.Item>
              )}
            />
          </MaterialWatchlistWrapper>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipients
        projectId={projectId}
        alertKey={alertKey}
        alertRules={alertRules}
        setAlertRules={setAlertRules}
      />
    </>
  )
}

const MaterialWatchlistWrapper = styled.div`
  margin-top: 0.25rem;
  min-width: 442px;
`
const MaterialWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`
